import {
  call, put, takeLeading, select, delay,
} from 'redux-saga/effects';
import humps from 'humps';

import CreditCardsApi from 'apis/supremeGolfApi/CreditCardsApi';
import SpreedlySessionActions, { SpreedlySessionTypes } from 'reducers/spreedlySession';

export const MAX_SESSION_RETRIES = 3;
export const RETRY_DELAY_MS = 1000;

/**
 * Fetches a new Spreedly Hosted Fields session from the backend.
 *
 * Lifecycle rules enforced here:
 * - takeLeading ensures only one request is in-flight at a time; concurrent
 *   dispatches share the same pending saga.
 * - Guard: if the current session is still valid (status === 'ready' and not
 *   yet expired), skip the API call entirely.
 * - Retry: automatically retries up to 3 times on failures, UNLESS the errorStatus
 *   is 400 (expected signal indicating Hosted Fields unavailable -> immediate legacy fallback).
 *
 * Error semantics:
 * - 400 → expected "Hosted Fields not available" signal → errorStatus: 400
 *         The switcher component uses this to fall back to the legacy iFrame.
 * - 429 → rate limited; do NOT fall back to legacy; show retry UI.
 * - 5xx / network → recoverable; show retry UI after exhausting retries.
 * - Other → error state with telemetry-friendly logging.
 */
export function* requestSpreedlySessionHandler() {
  const { status, expiresAtMs } = yield select((state) => state.spreedlySession);
  const checkoutFeatures = yield select(
    (state) => state.checkout?.features,
  );
  // Check both camelCase and snake_case keys
  const spreedlySessionsEnabled = checkoutFeatures?.spreedlySessions !== undefined
    ? checkoutFeatures?.spreedlySessions
    : checkoutFeatures?.spreedly_sessions;

  // Guard: skip if the current session is still valid
  if (status === 'ready' && expiresAtMs && Date.now() < expiresAtMs) {
    return;
  }

  if (spreedlySessionsEnabled === false) {
    yield put(
      SpreedlySessionActions.spreedlySessionError(
        'Spreedly sessions not supported',
        400,
      ),
    );
    return;
  }

  yield put(SpreedlySessionActions.spreedlySessionLoading());

  for (let attempt = 0; attempt <= MAX_SESSION_RETRIES; attempt += 1) {
    try {
      const data = yield call(CreditCardsApi.getSpreedlyAuthParams);
      // The API returns camelCased keys; Spreedly SDK expects snake_case
      const session = humps.decamelizeKeys(data.session) || {};

      // expires_at is a Unix seconds integer from the backend; convert to ms
      const rawExpiresAt = session.expires_at;
      let computedExpiresAtMs = null;
      if (rawExpiresAt) {
        const n = Number(rawExpiresAt);
        // Heuristic: values < 1e10 are seconds, >= 1e10 are already milliseconds
        computedExpiresAtMs = n < 1e10 ? n * 1000 : n;
      }

      yield put(SpreedlySessionActions.spreedlySessionSuccess(session, computedExpiresAtMs));
      return;
    } catch (err) {
      const errorStatus = err?.response?.status ?? null;
      const isNonRetryable = errorStatus === 400;
      const isLastAttempt = attempt === MAX_SESSION_RETRIES;

      if (isNonRetryable || isLastAttempt) {
        const message = err?.response?.data?.message
          || err?.response?.data?.error
          || (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors[0] : null)
          || (errorStatus === 429 ? 'Too many requests. Please try again later.' : null)
          || err?.message
          || 'Failed to initialize payment session. Please try again.';

        // Log unexpected auth failures for observability
        if (errorStatus === 401 || errorStatus === 403) {
          // eslint-disable-next-line no-console
          console.error('[Spreedly] Unexpected auth failure:', errorStatus, message);
        }

        yield put(SpreedlySessionActions.spreedlySessionError(message, errorStatus));
        return;
      }

      yield delay(RETRY_DELAY_MS);
    }
  }
}

function* requestSpreedlySessionWatcher() {
  // takeLeading: while a saga is running, any new REQUEST_SPREEDLY_SESSION
  // dispatches are ignored — prevents duplicate in-flight API calls.
  yield takeLeading(
    SpreedlySessionTypes.REQUEST_SPREEDLY_SESSION,
    requestSpreedlySessionHandler,
  );
}

export default [requestSpreedlySessionWatcher];
